import React from 'react';
import {
  StatTiles, Button, BottomSheet, Checkbox, CollapsibleSection,
  ErrorState, useToast,
} from '@parsina/framework';
import { SlidersHorizontal } from 'lucide-react';
import { C } from '../../constants/theme';
import { useTranslation } from '../../i18n/I18nProvider.jsx';
import Odontogram from '../Odontogram/Odontogram.jsx';
import { exportChartPng } from '../Odontogram/exportChart.js';
import { useDentalChart, useToothHistory } from '../../hooks/useDentalChart.js';
import { useTreatmentPlans } from '../../hooks/useVisit.js';
import ToothHistory from '../Odontogram/ToothHistory.jsx';
import {
  planTeethIndex, outstandingPlanTeeth, treatedByFdi, wholeMouthRecords, recordTitle,
} from '../../screens/Visit/Visit.shared.js';
import MobileToothPicker from './MobileToothPicker.jsx';
import MobileToothSheet from './MobileToothSheet.jsx';
import MobileChartLegend from './MobileChartLegend.jsx';
import { MobileSectionTitle } from './MobileKit.jsx';
import {
  MobileChartSkeleton, ToothHistorySkeleton,
} from '../../screens/PatientDetail/PatientDetail.skeleton.jsx';

// چارت دندان — the mobile section (§4C ⑤).
//
// ┌──────────────────────────────────────┐
// │ StatTiles 4-up   پوسیده · ترمیم · … │
// │ [⚙ تنظیمات نمودار] → BottomSheet     │
// │ <Odontogram/>  ← UNCHANGED, ltr      │
// │ MobileToothPicker  ← the ≥44px path  │
// │ CollapsibleSection «راهنما»           │
// │ MobileToothSheet (BottomSheet)        │
// └──────────────────────────────────────┘
//
// THE ODONTOGRAM IS RENDERED VERBATIM. Not forked, not wrapped in a transform,
// not given a `direction` or a logical property on its container, not
// pan/zoomed, not split per quadrant. It is viewBox-scaled at width:100% and
// already carries `style={{direction:'ltr'}}`, which is what keeps the patient's
// right on the screen's left in BOTH locales (CORE.md §9 — a patient-safety
// invariant, not styling). The chart is the OVERVIEW; MobileToothPicker is the
// touch-compliant way in. See that file for the full reasoning.
//
// The three chart CONTROLS move into a BottomSheet — §6 makes the sheet the
// default overlay for a task or a setting set, and three outline buttons in a
// row would eat the fold above the chart.

const MobilePatientChart = ({ patientId, clinicId }) => {
  const { t, formatDate, formatNumber } = useTranslation();
  const toast = useToast();
  const { teeth, byFdi, summary, loading, error, saving, record, reload } =
    useDentalChart({ patientId, clinicId });

  // The plan and the treatments, on the phone's chart too — the same hook the
  // visit and the desktop record use, so no two charts can disagree about what
  // is planned on a tooth or what was done to it.
  const { plans, records, catalog } = useTreatmentPlans({ patientId, clinicId });
  const planTeeth = React.useMemo(() => planTeethIndex(plans), [plans]);
  const plannedFdis = React.useMemo(() => outstandingPlanTeeth(planTeeth), [planTeeth]);
  const treatedFdis = React.useMemo(
    () => treatedByFdi(records, catalog), [records, catalog],
  );
  const wholeMouth = React.useMemo(() => wholeMouthRecords(records), [records]);

  const [selected, setSelected] = React.useState(null);
  const [focusSurface, setFocusSurface] = React.useState(null);
  const [showHistory, setShowHistory] = React.useState(false);
  const historyId = React.useId();

  // One read per tooth, fired only when the trail is asked for.
  const history = useToothHistory({
    patientId,
    toothFdi: selected,
    enabled: showHistory && !!selected,
  });

  // Folded away when the sheet moves to another tooth — otherwise one tooth's
  // history shows under another tooth's heading until the new read lands.
  React.useEffect(() => { setShowHistory(false); }, [selected]);

  const toothRecords = React.useMemo(
    () => (selected ? (records || []).filter((r) => r?.toothFdi === selected) : []),
    [records, selected],
  );
  const [showPrimary, setShowPrimary] = React.useState('auto');
  const [labelMode, setLabelMode] = React.useState('fdi');
  const [settingsOpen, setSettingsOpen] = React.useState(false);
  const [exporting, setExporting] = React.useState(false);
  const chartBoxRef = React.useRef(null);

  // The ONE way a tooth is opened — from the chart, or from the picker.
  const openTooth = React.useCallback((fdi, surface = null) => {
    setSelected(fdi);
    setFocusSurface(surface);
  }, []);

  const doExport = async () => {
    setExporting(true);
    try {
      const res = await exportChartPng({
        svgEl: chartBoxRef.current?.querySelector('svg'),
        patientId,
        header: `${patientId} — ${formatDate(Date.now(), 'numeric')}`,
      });
      // §7: a failure REPORTS. The desktop paints a hand-rolled red div; on
      // mobile the sheet is already closing, so the toast is the right channel.
      if (!res.ok) toast.error(t('chart.export.error'));
    } finally {
      setExporting(false);
      setSettingsOpen(false);
    }
  };

  // §7: the chart's OWN shape — tiles, the arch at its aspect, the tooth
  // picker — not a generic card stack (DESIGN.md §"Loading & skeleton").
  if (loading) {
    return <MobileChartSkeleton label={t('common.loading')} />;
  }

  if (error) {
    return (
      <div role="alert" data-state="error">
        <ErrorState
          title={t('chart.error.title')}
          description={t('chart.error.body')}
          onRetry={reload}
          retryLabel={t('common.actions.retry')}
        />
      </div>
    );
  }

  // Values are PRE-FORMATTED — §8: "Persian digits via the shared utils
  // everywhere a number renders". The kit renders `value` verbatim on purpose;
  // the digit seam belongs to the app.
  const tiles = [
    {
      key: 'decayed',
      label: t('chart.summary.decayed'),
      value: formatNumber(summary?.decayed ?? 0),
      solid: true,
    },
    {
      key: 'restored',
      label: t('chart.summary.restored'),
      value: formatNumber(summary?.restored ?? 0),
      color: C.navy,
    },
    {
      key: 'missing',
      label: t('chart.summary.missing'),
      value: formatNumber(summary?.missing ?? 0),
    },
    {
      key: 'dmft',
      label: t('chart.summary.dmft'),
      value: formatNumber(summary?.dmft ?? 0),
      color: C.teal,
    },
  ];

  return (
    <div data-testid="chart-content" data-state="content">
      <StatTiles tiles={tiles} data-testid="chart-stats" />

      <MobileSectionTitle
        style={{ marginTop: '0.875rem' }}
        count={formatNumber(summary?.chartedTeeth ?? 0)}
        action={(
          <Button
            variant="outline"
            onClick={() => setSettingsOpen(true)}
            data-testid="chart-settings-open"
            className="min-h-11 gap-1.5"
          >
            <SlidersHorizontal size={15} aria-hidden />
            {t('chart.mobile.settings')}
          </Button>
        )}
      >
        {t('chart.summary.charted')}
      </MobileSectionTitle>

      {/* No `direction`, no logical property, no overflow, no minWidth on this
          box — CORE.md §12.7. It is a plain white card the svg scales inside. */}
      <div
        ref={chartBoxRef}
        style={{
          background: C.card,
          border: `1px solid ${C.border}`,
          borderRadius: '0.75rem',
          padding: '0.5rem 0.25rem 0.25rem',
        }}
      >
        <Odontogram
          teeth={teeth}
          selected={selected}
          highlight={plannedFdis}
          treated={treatedFdis}
          planned={planTeeth}
          onSelect={(fdi) => openTooth(fdi)}
          onSurfaceSelect={(fdi, surface) => openTooth(fdi, surface)}
          showPrimary={showPrimary}
          labelMode={labelMode}
          t={t}
          formatDate={formatDate}
        />
      </div>

      {/* «کل دهان» — a جرم‌گیری belongs to no tooth and can carry no badge, and
          marking all 32 would bury the teeth that did have work. One line under
          the arch, because it is real work that otherwise leaves no trace. */}
      {wholeMouth.length > 0 && (
        <div
          data-testid="chart-whole-mouth"
          style={{
            marginTop: '0.5rem', fontSize: '0.6875rem', color: C.textSecondary,
            display: 'flex', gap: '0.375rem', flexWrap: 'wrap', alignItems: 'baseline',
            lineHeight: 1.8,
          }}
        >
          <span style={{ color: C.textMuted }}>{t('chart.wholeMouth.title')}{t('common.punct.colon')}</span>
          {wholeMouth.slice(0, 3).map((r, i) => (
            <span key={r.id}>
              {i > 0 ? '· ' : ''}{recordTitle(r, catalog) || t('visit.plan.untitledItem')}
            </span>
          ))}
          {wholeMouth.length > 3 && (
            <span style={{ color: C.textMuted }}>
              {t('chart.tooltip.andMore', { count: formatNumber(wholeMouth.length - 3) })}
            </span>
          )}
        </div>
      )}

      <div style={{ marginTop: '0.875rem' }}>
        <MobileSectionTitle>{t('chart.mobile.pickTooth')}</MobileSectionTitle>
        <div style={{ fontSize: '0.6875rem', color: C.textMuted, marginBottom: '0.625rem', lineHeight: 1.8 }}>
          {t('chart.mobile.tapHint')}
        </div>
        <MobileToothPicker
          teeth={teeth}
          byFdi={byFdi}
          showPrimary={showPrimary}
          selected={selected}
          onPick={(fdi) => openTooth(fdi)}
          t={t}
        />
      </div>

      <div style={{ marginTop: '0.875rem' }}>
        <CollapsibleSection title={t('chart.mobile.legend')}>
          <MobileChartLegend t={t} />
        </CollapsibleSection>
      </div>

      <BottomSheet
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        title={t('chart.mobile.settings')}
        closeLabel={t('mobile.close')}
        data-testid="chart-settings-sheet"
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.875rem' }}>
          <Checkbox
            checked={showPrimary === true}
            onChange={(next) => setShowPrimary(next ? true : 'auto')}
            label={t('chart.showPrimary')}
            data-testid="chart-settings-primary"
            className="min-h-11"
          />
          <Checkbox
            checked={labelMode === 'both'}
            onChange={(next) => setLabelMode(next ? 'both' : 'fdi')}
            label={t('chart.showUniversal')}
            data-testid="chart-settings-universal"
            className="min-h-11"
          />
          <Button
            variant="outline"
            onClick={doExport}
            disabled={exporting}
            data-testid="chart-settings-export"
            className="w-full min-h-11"
          >
            {t('chart.export.button')}
          </Button>
        </div>
      </BottomSheet>

      <MobileToothSheet
        open={!!selected}
        fdi={selected}
        state={selected ? byFdi[selected] : null}
        focusSurface={focusSurface}
        saving={saving}
        onSave={record}
        onClose={() => { setSelected(null); setFocusSurface(null); }}
        history={(
          /* «تاریخچه دندان» — the half of the record the chart cannot draw. The
             folded state says a tooth is crowned; it does not say when, by whom,
             or what was there before. Collapsed by default: the sheet is opened
             to CHART, and an 85vh scroll region that starts with a trail buries
             the editor. */
          <div style={{ marginBottom: '0.875rem' }}>
            <Button
              variant="outline"
              aria-expanded={showHistory}
              aria-controls={historyId}
              onClick={() => setShowHistory((v) => !v)}
              data-testid="tooth-sheet-history-toggle"
              className="w-full min-h-11"
            >
              {t('visit.chart.history')}
            </Button>

            {showHistory && (
              <div id={historyId} style={{ marginTop: '0.5rem' }}>
                {history.loading
                  ? <ToothHistorySkeleton label={t('common.loading')} />
                  : (
                    <ToothHistory
                      fdi={selected}
                      events={history.events}
                      records={toothRecords}
                      catalog={catalog}
                      error={!!history.error}
                      t={t}
                      formatDate={formatDate}
                    />
                  )}
              </div>
            )}
          </div>
        )}
        t={t}
      />
    </div>
  );
};

export default MobilePatientChart;
